<template>
  <div>
    <div class="w-full flex flex-col items-center relative pt-12 lg:pt-24">
      <h1 class="font-sourceSans-light text-3xl md:text-4xl xl:text-5xl text-gray-600 mb-4 md:mb-8">{{ strings.meetus }}</h1>
      <div class="mb-8 md:mb-12">
        <a 
          v-for="country in countries" 
          :data-value="country.value" 
          :key="country.value" 
          :selected="country.selected"
          class="py-1-5 px-3 font-sourceSans-regular text-green-500 uppercase text-xxs tracking-wide border border-gray-353 rounded-full mx-2 hover:bg-green-100 transition-colors duration-200 cursor-pointer"
          :class="country.value === activeCountryId ? 'bg-green-100' : 'bg-white'"
          @click="handleCountryChange($event)"
        >
          {{ country.title }}
        </a>
      </div>
      <div class="offices">
        <div class="offices__tools">
          <div class="offices__tools__header text-gray-500">
            <h2 class="font-sourceSans-light text-xxl md:text-2xl">{{ strings.findAnOffice }}</h2>
            <p class="font-sourceSans-regular text-md md:text-base tracking-slight">{{ strings.results }} ({{ officesOptions.length }})</p>
          </div>
          <div class="my-office" :class="{'loading': officesLoading}">
            <ul class="offices-wrapper">
              <li v-for="(office) in officesOptions" :key="office.id" :id="'office-' + office.id" class="flex flex-col align-start font-sourceSans-regular pb-6">
                <figure class="relative pb-aspect67 bg-green-500 rounded-medium overflow-hidden mb-8">
                  <img :src="office.image" alt="" loading="lazy" class="absolute w-full h-full top-0 left-0">
                </figure>
                <div class="flex items-start justify-between mb-3">
                  <h5 class="font-sourceSans-light text-xxl md:text-2xl leading-xs text-gray-600">{{ office.title }}</h5>
                  <div class="cursor-pointer" @click="handleOfficeChange(office.id, false)">
                    <svg width="30" height="31" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.66663 13.7688C3.66663 7.5556 8.70342 2.5188 14.9166 2.5188C21.1298 2.5188 26.1666 7.5556 26.1666 13.7688C26.1666 20.0187 21.1373 24.825 15.4321 27.4076C15.1045 27.5559 14.7288 27.5559 14.4011 27.4076C8.69591 24.825 3.66663 20.0187 3.66663 13.7688Z" :fill="office.id === activeOfficeId ? '#046A40' : '#ffffff'" stroke="#D1CEC9" stroke-width="0.4" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M20.9168 17.7994C19.4878 15.4986 18.0649 13.2038 16.6541 10.9089C14.9769 10.9089 13.2997 10.8968 11.6225 10.9089C12.567 12.4166 11.6225 10.915 12.561 12.4166C9.66675 17.7692 12.5428 12.4227 9.66675 17.7813V17.7873C11.8647 17.8055 9.66675 17.7813 11.8828 17.7873C12.6034 16.4431 11.9858 17.6178 12.6821 16.3039L16.7691 16.3039C16.0426 17.6662 16.7268 16.3765 15.9638 17.7934L20.9168 17.7934V17.7994ZM15.5824 14.6388C13.7417 14.6448 15.4189 14.6448 13.6024 14.6388H13.5903C14.4622 13.0463 13.6448 14.5237 14.5409 12.9192C15.54 14.5237 15.6066 14.6085 15.5824 14.6388Z" :fill="office.id === activeOfficeId ? '#ffffff' : '#046A40'"/>
                      <path d="M12.9121 10.3155C12.016 8.83204 12.906 10.3155 11.9675 8.77149C13.8748 8.76543 14.2321 8.77149 15.3098 8.77149C16.2786 10.2852 15.3462 8.83204 16.2907 10.3155L12.9121 10.3155Z" :fill="office.id === activeOfficeId ? '#ffffff' : '#046A40'"/>
                    </svg>
                  </div>
                </div>
                <p v-if="office.officeInfo" class="text-md tracking-normal leading-loose text-gray-600">{{ office.officeInfo }}</p>
                <p class="text-md tracking-normal leading-loose text-gray-600">{{ office.address }}</p>
                <a class="text-md tracking-normal leading-loose text-gray-600" :href="`tel:${office.phone}`">{{ office.phone }}</a>
                <a class="text-md tracking-normal leading-loose text-gray-600" :href="`mailto:${office.email}`">{{ office.email }}</a>
                <div class="text-md tracking-normal leading-loose text-gray-600 mt-3" v-for="item in office.officeOpeningHours" :key="item">
                  <p>{{ item.description }}</p>
                  <p>{{ item.days }}</p>
                  <p>{{ item.hours }}</p>
                </div>
              </li>
            </ul>
            
            <div class="my-office__overlay">
              <div class="map-office-loader">
                <div class="map-office-loader__inner">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="map" style="height: 100%;"></div>
      </div>
    </div>
    
    <div class="max-w-container mx-auto" v-if="siteVariables && (siteVariables.language === 'fi-FI' || siteVariables.language === 'sv-FI')">
      <div class="flex flex-col items-center py-12 md:py-16 xl:py-24">
        <h2 class="font-sourceSans-light text-3xl md:text-4xl xl:text-5xl text-gray-600 mb-4 md:mb-8">{{ strings.searchPeople }}</h2>
        <div class="search--meet-us-container w-full max-w-132 mb-6 md:mb-10" :class="{'loading': officeStaffLoading}">
          <input 
            v-model="personnelSearchString" 
            type="text" 
            class="search--meet-us w-full text-xxl md:text-2xl py-5 pl-18 pr-5 shadow-card rounded-regular"
            :placeholder="strings.peopleSearchPlaceholder"
          >
        </div>
        <div class="w-screen pl-4-5 sm:pl-0 mb-8 md:mb-12 flex flex-wrap gap-y-5 max-w-220 sm:justify-center">
          <a 
            v-for="officeOption in officesOptions"
            :key="officeOption.id" 
            :href="'#staff-office-' + officeOption.id" 
            :data-office-id="officeOption.id"
            class="office-link flex-shrink-0 py-1-5 px-3 bg-white font-sourceSans-regular text-green-500 uppercase text-xxs tracking-wide border border-gray-353 rounded-full mr-3 md:mx-2 hover:bg-green-100 transition-colors duration-200"
          >
            {{ officeOption.title }}
          </a>
        </div>

        <div class="w-full office-staff">
          <!-- <div class="office-staff__content"> -->
            <div v-html="officeStaffMarkup"></div>
          <!-- </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader';
import {debounce} from '../helpers/_debounce'

  export default {
    props: {
      apiKey: {
        type: String,
        required: true,
      },

      offices: {
        type: Array,
        required: true,
      },

      countries: {
        type: Array,
        required: true,
      },

      strings: {
        type: Object,
        required: true,
      }
    },

    data() {
      return {
        // map: {},
        markers: {},
        officesOptions: [],
        activeOfficeId: 0,
        activeCountryId: 0,
        officeMarkup: '',
        officeStaffMarkup: '',
        officeSelectOpen: false,
        officesLoading: true,
        officeStaffLoading: true,
        bounds: undefined,
        personnelSearchString: '',
        debouncedInput: '',
        siteVariables: ALANDSBANKEN
      }
    },

    created: function() {
      this.initMap();
    },

    /* updated: function() {
      $(document).foundation('equalizer', 'reflow');
    }, */

    computed: {
      selectedOffice() {
        const officesOptions = this.officesOptions;

        if (officesOptions.find(officeOption => officeOption.id === this.activeOfficeId)) {
          return officesOptions[this.activeOfficeId];
        }

        return false;
      },
    },

    methods: {
      initMap() {
        const loader = new Loader({
          apiKey: this.apiKey,
          version: "weekly"
        });

      loader
        .importLibrary('maps')
        .then(async ({Map}) => {
          const mapOptions = {
            disableDefaultUI: true,
            disableDoubleClickZoom: false,
            mapTypeControl: false,
            maxZoom: 26,
            minZoom: 3,
            overviewMapControl: false,
            panControl: false,
            rotateControl: false,
            scaleControl: false,
            scrollwheel: false,
            streetViewControl: false,
            zoom: 4,
            zoomControl: true,
            zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER,
                style: window.google.maps.ZoomControlStyle.LARGE
            },
            mapId: 'DEMO_MAP_ID'
          };

          this.bounds = new window.google.maps.LatLngBounds();
          const map = new Map(document.getElementById("map"), mapOptions);
          this.map = map;
          
          const { AdvancedMarkerElement } = await loader.importLibrary("marker");

          return AdvancedMarkerElement;
        })
        .catch((e) => {
          // do something
        }).then((AdvancedMarkerElement) => {
          // Set active country ID
          this.countries.forEach((country, i) => {
            if (country.selected) {
              this.activeCountryId = country.value
            };
          });
          
          this.offices.forEach((office, i) => {
            this.setMarker(office, office.current, AdvancedMarkerElement, map);
          });
        });
      },

      setMarker(office, current, advancedMarkerElement, map) {
        if (office.lat && office.lng) {
            const marker = new advancedMarkerElement({
              position: {
                lat: parseFloat(office.lat),
                lng: parseFloat(office.lng),
              }
            });

            marker.addListener('click', () => {
              this.handleOfficeChange(office.id, true);
            });

            this.markers[office.id] = marker;

            if (current === 1) {
              this.handleOfficeChange(office.id, true);
            }
        }
      },

      handleCountryChange({target}) {
        this.activeCountryId = parseInt(target.dataset.value);
      },

      handleOfficeChange(id, scroll) {
        this.activeOfficeId = id;

        const officeElement = document.getElementById(`office-${id}`);
        const officeListHeader = document.querySelector('.offices__tools__header');
        if (scroll && officeElement) {
          let offsetTop = officeListHeader ? officeElement.offsetTop - officeListHeader.clientHeight : officeElement.offsetTop;
          officeElement.closest('.my-office').scrollTop = offsetTop;
        }
      },

      handleOfficeSelectClick() {
        this.officeSelectOpen = !this.officeSelectOpen;
      },
      
      updateLinks(text) {
        var tempOfficesElement = document.createElement('div');
        tempOfficesElement.innerHTML = text;
        const links = document.querySelectorAll('[data-office-id]');
        links.forEach(element => {
          const officeElement = tempOfficesElement.querySelector(`#staff-office-${element.dataset.officeId}`);
          if (officeElement && officeElement.children.length < 1) {
            element.classList.add('hidden');
          } else {
            element.classList.remove('hidden');
          }
        });
      }
    },

    watch: {
      activeOfficeId(id, oldId) {
        this.officesLoading = true;
        this.officeStaffLoading = true;

        // Set marker icons for the selected and not selected markers,
        // and pan to the selected marker.
        Object.entries(this.markers).forEach((entry, i) => {
          const [key, marker] = entry;
          if (id == key) {
            const markerIcon = document.createElement('img');
            markerIcon.style.width = '84px';
            markerIcon.style.height = '101px';
            markerIcon.src = assetsUrl + 'images/map-marker-selected.png';
            marker.content = markerIcon;

            // If the old office ID is in the offices options list
            // it means the country hasn't changed and we can pan to
            // the selected marker.
            if (this.officesOptions.find(officeOption => officeOption.id === oldId)) {
              this.map.panTo(marker.position);
              if (this.activeCountryId === 63) {
                this.map.setZoom(12);
              } else {
                if (this.map.getZoom() < 10) {
                  this.map.setZoom(10);
                };
              }
            }
          } else {
            const markerIcon = document.createElement('img');
            markerIcon.style.width = '60px';
            markerIcon.style.height = '72px';
            markerIcon.src = assetsUrl + 'images/map-marker.png';
            marker.content = markerIcon;
          }
        });

        
        if (this.activeCountryId === 65 || this.activeCountryId === '65') {
          // Get office staff markup for the selected office.
          fetch(siteUrl + 'offices/load-personnel?' + new URLSearchParams({
            officeId: id
          }), {
            method: 'GET',
            headers: {
              'x-requested-with': 'XMLHttpRequest'
            }
          })
          .then(response => response.text())
          .then(text => {
            this.officeStaffMarkup = text;
            this.updateLinks(text);
            this.officeStaffLoading = false;
            this.officesLoading = false;
          });
        } else {
          this.officeStaffLoading = false;
          this.officesLoading = false;
        }
      },

      personnelSearchString: debounce(function (newVal) {
        this.debouncedInput = newVal
      }, 500),

      debouncedInput(newVal) {
        this.officeStaffLoading = true;
        // Get office staff markup for the selected office.
        fetch(siteUrl + 'offices/load-personnel?' + new URLSearchParams({
          officeId: this.activeOfficeId,
          personnelSearchString: newVal
        }), {
          method: 'GET',
          headers: {
            'x-requested-with': 'XMLHttpRequest'
          }
        })
        .then(response => response.text())
        .then(text => {
          this.officeStaffMarkup = text;
          this.updateLinks(text);
          this.officeStaffLoading = false;
        });
      },

      activeCountryId(id) {
        // Get office options for the selected country.
        fetch(siteUrl + 'api/office-options?' + new URLSearchParams({
          countryId: id
        }), {
          method: 'GET',
          headers: {
            'x-requested-with': 'XMLHttpRequest'
          }
        })
        .then(response => response.json())
        .then(json => {

          let offices = [];
          for (var i = 0; i < json['data'].length; i++) {
            const office = json['data'][i];
            offices.push(office);
            // offices[office.id] = office;
          }
          this.officesOptions = offices;
        });
      },

      bounds() {
        let first = true;
        const bounds = this.bounds;
        const offices = this.officesOptions;

        Object.entries(this.markers).forEach((entry, i) => {
          const [key, marker] = entry;
          const keyInt = parseInt(key);
          const markerOffice = offices.find((office) => office.id === keyInt);
          if (markerOffice) {

            // Set first office as active.
            if (!this.selectedOffice && first) {
              this.activeOfficeId = markerOffice.id;
              first = false;
            }
            bounds.extend(marker.position);
            marker.map = this.map;
          } else {
            marker.map = null;
          }
        });

        this.map.panTo(bounds.getCenter());
        this.map.fitBounds(bounds);
      },

      officesOptions(offices) {
        if(window.google) {
          this.bounds = new window.google.maps.LatLngBounds();
        }

      }
    }
  }
</script>
